import * as React from "react";
import { cn } from "../../lib/cn";
import type {
  ActionFieldError,
  ActionFieldSpec,
  ActionFieldValue,
} from "../ActionFieldEditor";

/**
 * envelope-primitives — DRY building blocks shared by every "compose"
 * action preview (email envelope, message envelope, future PEC / WhatsApp /
 * Salesforce-note envelopes — May 26, 2026).
 *
 * Why this file exists: ``EmailSendDraftPreview`` and ``SendMessagePreview``
 * each owned their own copies of ``ROW`` / ``LABEL`` / ``INPUT_BASE`` / the
 * ``<Row>`` primitive / ``asString`` / ``specOf`` / the "isControlled +
 * liveValues + commit" boilerplate. Three envelopes from now (PEC, WhatsApp,
 * Salesforce note) duplicating the same 60 LoC would be untenable — extract
 * the primitives BEFORE the third copy lands, not after.
 *
 * No new behaviour; mechanical DRY consolidation. Each envelope still owns
 * its own field list (``from_account / to / cc / bcc / subject / body`` vs
 * ``channel_name / message``), its own header icon + label, and its own
 * read-only fallback layout.
 */

// ---------------------------------------------------------------------------
// CSS class constants — keep the visual rhythm identical across envelopes
// ---------------------------------------------------------------------------

export const ENVELOPE_CLASSES = {
  row: "grid grid-cols-[88px,1fr] items-start gap-3 py-1.5",
  label: "pt-1.5 font-medium text-[11px] text-ink-3 uppercase tracking-wide",
  input:
    "w-full rounded border border-hairline bg-transparent px-2 py-1.5 text-sm text-ink-1 placeholder:text-ink-3 focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/30 disabled:cursor-not-allowed disabled:opacity-60",
  help: "mt-1 text-[11px] text-ink-3",
  err: "mt-1 text-[11px] text-red-600",
  card: "divide-y divide-hairline rounded-md border border-hairline bg-card px-3 py-1.5",
  cardReadonly: "rounded-md border border-hairline bg-card px-3 py-2.5",
  body: "min-h-[180px] resize-y leading-relaxed",
} as const;

// ---------------------------------------------------------------------------
// Value coercion — every textarea / input expects a string but the wire
// shape allows number / boolean / null too (see ``ActionFieldValue``).
// ---------------------------------------------------------------------------

export const asString = (v: ActionFieldValue | undefined): string =>
  typeof v === "string" ? v : v === null || v === undefined ? "" : String(v);

/**
 * MULTISELECT fields (``to`` / ``cc`` / ``bcc``) ride the wire as either a
 * comma-joined string or a JS array depending on which synth pipeline emitted
 * them. Normalise to the displayed form (comma-separated text) so a single
 * ``<input>`` per row stays simple; the BE validator handles both shapes
 * (``unified_schema_builder.validate_executed_payload``).
 */
export const asRecipientText = (v: ActionFieldValue | undefined): string => {
  if (Array.isArray(v)) return v.filter(Boolean).join(", ");
  return asString(v);
};

// ``asStringArray`` — re-export from the shared coercion module so the
// envelope's ``A`` / ``Cc`` / ``Ccn`` rows and ``ActionFieldEditor``'s
// MultiSelect / Recipients renderers consume the SAME narrowing logic.
// Single source of truth: ``packages/ui/src/lib/valueCoercion.ts``.
export { asStringArray } from "../../lib/valueCoercion";

// ---------------------------------------------------------------------------
// Schema lookup — every envelope picks specs out of the full schema by name
// ---------------------------------------------------------------------------

export function specOfName<Name extends string>(
  schema: readonly ActionFieldSpec[] | undefined,
  name: Name,
): ActionFieldSpec | undefined {
  return schema?.find((s) => s.name === name);
}

// ---------------------------------------------------------------------------
// <EnvelopeRow> — label-on-left + input-on-right primitive used by every
// envelope row (Da, A, Cc, Ccn, Oggetto, In, …). Renders error OR helper
// (errors win).
// ---------------------------------------------------------------------------

export interface EnvelopeRowProps {
  readonly label: string;
  readonly children: React.ReactNode;
  readonly help?: string;
  readonly error?: ActionFieldError;
}

export const EnvelopeRow: React.FC<EnvelopeRowProps> = ({
  label,
  children,
  help,
  error,
}) => (
  <div className={ENVELOPE_CLASSES.row}>
    <span className={ENVELOPE_CLASSES.label}>{label}</span>
    <div>
      {children}
      {error ? (
        <p className={ENVELOPE_CLASSES.err}>{error.message}</p>
      ) : help ? (
        <p className={ENVELOPE_CLASSES.help}>{help}</p>
      ) : null}
    </div>
  </div>
);
EnvelopeRow.displayName = "EnvelopeRow";

// ---------------------------------------------------------------------------
// useEnvelopeCommit — controlled-value hook every envelope uses to merge
// a single field-name change into the live values map and emit the new
// dict to the parent. Returns a stable callback so the inputs don't
// re-mount on every keystroke.
// ---------------------------------------------------------------------------

export function useEnvelopeCommit<Name extends string>(
  values: Readonly<Record<string, ActionFieldValue>> | undefined,
  onChange: ((next: Record<string, ActionFieldValue>) => void) | undefined,
): (name: Name, value: ActionFieldValue) => void {
  return React.useCallback(
    (name: Name, value: ActionFieldValue) => {
      if (!values || !onChange) return;
      onChange({ ...values, [name]: value });
    },
    [values, onChange],
  );
}

// ---------------------------------------------------------------------------
// EnvelopeHeader — small "Bozza email" / "Messaggio" eyebrow with icon
// ---------------------------------------------------------------------------

export interface EnvelopeHeaderProps {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly className?: string;
}

export const EnvelopeHeader: React.FC<EnvelopeHeaderProps> = ({
  icon,
  label,
  className,
}) => (
  <div className={cn("flex items-center gap-2 text-ink-3 text-xs", className)}>
    {icon}
    <span>{label}</span>
  </div>
);
EnvelopeHeader.displayName = "EnvelopeHeader";
